import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Modal, SpinningLoader } from "risa-oasis-ui_v2";
import { getFileDownloadUrl } from "../../../api/firebase/firestoreService";
import { RootState } from "../../../redux/store/store";
import { InfoIcon } from "../../../svg/info-icon";
import PdfRender from "../../pdfRender/pdfRender";

interface DocViewerModalProps {
  fileUrl?: string;
  onClose: () => void;
  type: "clinical_attachment" | "cmm_screenshot";
  shouldLogEvent?: boolean;
  screenName?: string;
  formName?: string;
  externalLoading?: boolean;
  showNoScreenshotMessage?: boolean;
}

const DocViewerModal = ({
  fileUrl,
  onClose,
  type,
  shouldLogEvent,
  screenName,
  formName,
  externalLoading = false,
  showNoScreenshotMessage = false,
}: DocViewerModalProps) => {
  const { metaData } = useSelector((state: RootState) => state.modalSliceNew);
  const [fetchedFileUrl, setFetchedFileUrl] = useState<string>("");
  const [isInternalLoading, setIsInternalLoading] = useState<boolean>(false);
  const [imageLoadFailed, setImageLoadFailed] = useState<boolean>(false);

  const effectiveFileUrl = fileUrl || fetchedFileUrl;
  const isLoading = externalLoading || isInternalLoading;

  useEffect(() => {
    setImageLoadFailed(false);
  }, [effectiveFileUrl]);

  useEffect(() => {
    const fetchFileUrl = async () => {
      if (!metaData?.filePath) return;

      setIsInternalLoading(true);
      try {
        const downloadUrl = await getFileDownloadUrl(metaData.filePath);
        if (downloadUrl) {
          setFetchedFileUrl(downloadUrl);
        }
      } catch (error) {
        console.error("Error fetching file URL:", error);
      } finally {
        setIsInternalLoading(false);
      }
    };

    if (metaData?.filePath) {
      setIsInternalLoading(true);
      fetchFileUrl();
    }
  }, [metaData?.filePath]);

  const handleDownload = () => {
    if (effectiveFileUrl) {
      window.open(effectiveFileUrl, "_blank");

      onClose();
    }
  };

  return (
    <Modal
      dialogId={"doc-viewer-modal"}
      onSave={handleDownload}
      title={
        metaData?.title
          ? metaData?.title
          : type === "clinical_attachment"
            ? "Clinical Document"
            : "Screenshot"
      }
      saveButtonText={"Download"}
      cancelText={"Cancel"}
      heightPercentage={90}
      onCancel={onClose}
      onClose={onClose}
      hideFooter={metaData?.hideFooter ?? type === "cmm_screenshot"}
    >
      <div className="doc-viewer-modal--container h-full w-full overflow-scroll">
        {isLoading || externalLoading ? (
          <div className="flex h-full w-full items-center justify-center gap-4">
            <SpinningLoader />
            <p className="text-gray-600">Loading document...</p>
          </div>
        ) : showNoScreenshotMessage ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3">
            <InfoIcon width="48" height="48" stroke="#6B7280" strokeWidth="1" />
            <p className="text-lg text-gray-600">
              No Screenshot found for this order
            </p>
          </div>
        ) : effectiveFileUrl ? (
          type === "cmm_screenshot" && !imageLoadFailed ? (
            <div className="flex h-full w-full items-center justify-center overflow-auto bg-gray-50 p-4">
              <img
                src={effectiveFileUrl}
                alt="CMM Screenshot"
                className="max-h-full max-w-full object-contain"
                onError={() => setImageLoadFailed(true)}
              />
            </div>
          ) : (
            <PdfRender file={effectiveFileUrl} />
          )
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <p className="text-gray-600">No document available</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DocViewerModal;
